import { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  ArrowPathIcon,
  CurrencyDollarIcon,
  GiftIcon,
  XCircleIcon,
  HeartIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { EyeIcon } from '@heroicons/react/24/solid';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { DonationsService } from '@/services/donations.service';
import type { FoodDonation, MonetaryDonation, DonationFilter, MonetaryDonationFilter } from '@/services/donations.service';

export default function Donations() {
  const { user } = useAuthV2();
  const [activeTab, setActiveTab] = useState('food'); // 'food' or 'monetary'
  const [foodDonations, setFoodDonations] = useState<FoodDonation[]>([]);
  const [monetaryDonations, setMonetaryDonations] = useState<MonetaryDonation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  useEffect(() => {
    if (activeTab === 'food') {
      fetchFoodDonations();
    } else {
      fetchMonetaryDonations();
    }
  }, [activeTab, searchTerm, statusFilter]);
  
  const fetchFoodDonations = async () => {
    setIsLoading(true);
    try {
      if (!user?.business_id) {
        setFoodDonations([]);
        setIsLoading(false);
        return;
      }
      
      // Create filter object based on current search and status filters
      const filter: DonationFilter = {
        searchTerm: searchTerm || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter as any
      };
      
      // Fetch food donations using the service
      const donations = await DonationsService.getFoodDonations(user.business_id, filter);
      setFoodDonations(donations);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching food donations:', error);
      setFoodDonations([]);
      setIsLoading(false);
    }
  };
  
  const fetchMonetaryDonations = async () => {
    setIsLoading(true);
    try {
      if (!user?.business_id) {
        setMonetaryDonations([]);
        setIsLoading(false);
        return;
      }
      
      // Create filter object based on current search
      const filter: MonetaryDonationFilter = {
        searchTerm: searchTerm || undefined
      };
      
      // Fetch monetary donations using the service
      const donations = await DonationsService.getMonetaryDonations(user.business_id, filter);
      setMonetaryDonations(donations);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching monetary donations:', error);
      setMonetaryDonations([]);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-navy-blue">Donations</h1>
          </div>
          
          {/* Tabs */}
          <div className="flex space-x-4 mb-6">
            <button
              onClick={() => setActiveTab('food')}
              className={`flex items-center px-4 py-2 rounded-lg ${activeTab === 'food' ? 'bg-navy-blue text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              <GiftIcon className="h-5 w-5 mr-2" />
              Food Donations
            </button>
            <button
              onClick={() => setActiveTab('monetary')}
              className={`flex items-center px-4 py-2 rounded-lg ${activeTab === 'monetary' ? 'bg-navy-blue text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
              Monetary Donations
            </button>
          </div>
          
          {/* Filters */}
          <div className="bg-white rounded-lg shadow-md p-4 mb-6">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search donations..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-blue focus:border-sky-blue"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              {activeTab === 'food' && (
                <div className="sm:w-48">
                  <select
                    className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-sky-blue focus:border-sky-blue"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="accepted">Accepted</option>
                    <option value="picked_up">Picked Up</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              )}
              
              <button
                onClick={() => activeTab === 'food' ? fetchFoodDonations() : fetchMonetaryDonations()}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-blue hover:bg-teal focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue mt-2 sm:mt-0"
              >
                <ArrowPathIcon className="h-4 w-4 mr-1" />
                Refresh
              </button>
            </div>
          </div>
          
          {/* Food Donations Content */}
          {activeTab === 'food' && (
            <div className="space-y-6">
              {isLoading ? (
                <div className="flex justify-center py-6">
                  <ArrowPathIcon className="h-8 w-8 text-navy-blue animate-spin" />
                </div>
              ) : foodDonations.length > 0 ? (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {foodDonations.map((donation) => (
                        <tr key={donation.id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {donation.business_name}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            <div className="max-w-xs overflow-hidden">
                              {donation.items.length > 1 ? (
                                <div>
                                  <span className="font-medium">{donation.items[0].name}</span>
                                  <span className="text-gray-400 ml-1">+{donation.items.length - 1} more</span>
                                </div>
                              ) : donation.items.length === 1 ? (
                                donation.items[0].name
                              ) : (
                                <span className="text-gray-400">No items</span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {DonationsService.formatCurrency(donation.totalValue)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {DonationsService.formatDate(donation.date)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              {donation.status === 'pending' && <ClockIcon className="h-5 w-5 text-yellow-500" />}
                              {donation.status === 'accepted' && <CheckCircleIcon className="h-5 w-5 text-blue-500" />}
                              {donation.status === 'picked_up' && <HeartIcon className="h-5 w-5 text-green-500" />}
                              {donation.status === 'cancelled' && <XCircleIcon className="h-5 w-5 text-red-500" />}
                              <span className={`ml-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${donation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : donation.status === 'accepted' ? 'bg-blue-100 text-blue-800' : donation.status === 'picked_up' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {donation.status === 'pending' ? 'Pending' : donation.status === 'accepted' ? 'Accepted' : donation.status === 'picked_up' ? 'Picked Up' : 'Cancelled'}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                            <button
                              className="text-blue-600 hover:text-blue-900 bg-blue-50 px-2 py-1 rounded flex items-center"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-500">
                      Showing <span className="font-semibold">{foodDonations.length}</span> {foodDonations.length === 1 ? 'donation' : 'donations'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md p-6 text-center">
                  <div className="flex flex-col items-center justify-center py-6">
                    <GiftIcon className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No food donations found</h3>
                    <p className="text-gray-500">There are no food donations matching your filters.</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Monetary Donations Content */}
          {activeTab === 'monetary' && (
            <div className="space-y-6">
              {isLoading ? (
                <div className="flex justify-center py-6">
                  <ArrowPathIcon className="h-8 w-8 text-navy-blue animate-spin" />
                </div>
              ) : monetaryDonations.length > 0 ? (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Donor</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {monetaryDonations.map((donation) => (
                        <tr key={donation.id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {donation.isAnonymous ? (
                              <span className="text-gray-500 italic">Anonymous</span>
                            ) : (
                              donation.donor_name
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-medium">
                            {DonationsService.formatCurrency(donation.amount)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {DonationsService.formatDate(donation.date)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {donation.message ? (
                              <div className="max-w-xs overflow-hidden truncate">
                                {donation.message}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">No message</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                            <button
                              className="text-blue-600 hover:text-blue-900 bg-blue-50 px-2 py-1 rounded flex items-center"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" /> View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200">
                    <div className="text-sm font-medium text-gray-500">
                      Showing <span className="font-semibold">{monetaryDonations.length}</span> {monetaryDonations.length === 1 ? 'donation' : 'donations'}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md p-6 text-center">
                  <div className="flex flex-col items-center justify-center py-6">
                    <CurrencyDollarIcon className="h-12 w-12 text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-1">No monetary donations found</h3>
                    <p className="text-gray-500">There are no monetary donations matching your filters.</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
